import { createRef, Component } from 'react'
import * as cn from 'classnames'
import * as throttle from 'lodash/throttle'
import { Cancelable } from 'lodash'
import { connect } from 'react-redux'

import { Anchor, AnchorActivationOptions, AnchorPlacement } from 'interfaces/navigation'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { State } from 'interfaces/state'
import { SubtreeTrackerContextType, SubtreeTrackerContext } from 'components/utils/SubtreeTracker'
import { QAMarked } from 'interfaces/component'
import { extractAnchor, getAnchorPage, getAnchorBlock } from 'helpers/pageHelpers'
import { BlockPositionRelativeToWindow, isElementInViewport } from 'helpers/styleHelpers'
import withContext from 'hocs/withContext/withContext'

const EMPIRIC_SCROLL_MARGIN = 72
const SCROLL_DELTA: { [p: string]: number } = {
  feed: 60,
}

const cancelableMixin: Cancelable = {
  cancel: () => { /* do nothing */ },
  flush: () => { /* do nothing */ },
}

export type AnchorStateProps = {
  activateOptions: null | AnchorActivationOptions
}

export type AnchorDispatchProps = {
  discardActiveAnchor: () => void
}

export type AnchorOwnProps = QAMarked & {
  placement: Anchor
  preventScrollThrottling?: boolean
  subtreeTracker: SubtreeTrackerContextType
  className?: string
}

type AnchorProps =
  & AnchorStateProps
  & AnchorDispatchProps
  & AnchorOwnProps

type AnchorState = {
  isSubscribed: boolean
}

/**
 * @deprecated
 * Use PageAnchorHandler
 */
class AnchorComponent extends Component<AnchorProps, AnchorState> {
  state = {
    isSubscribed: false,
  }

  private rootRef = createRef<HTMLDivElement>()

  private scrollIsBlocked: boolean

  private handleScrollThrottled: EventListener & Cancelable

  render () {
    const { children, qaMarker, className } = this.props

    return (
      <div className={ cn('anchor', className) } ref={ this.rootRef } data-qa-marker={ qaMarker }>
        { children }
      </div>
    )
  }

  componentDidMount () {
    this.handleScrollThrottled = this.props.preventScrollThrottling
      ? Object.assign(this.handleScroll, cancelableMixin)
      : throttle(this.handleScroll, 50)

    this.handleComponentUpdate()
  }

  componentDidUpdate () {
    this.handleComponentUpdate()
  }

  componentWillUnmount () {
    this.destroyEventBindings()
    this.handleScrollThrottled.cancel()
  }

  private resolveNearest = (rect: ClientRect): BlockPositionRelativeToWindow => {
    const isCenterUpper = (rect.top + rect.height) < window.innerHeight / 2
    return isCenterUpper ? 'start' : 'end'
  }

  private getWindowScrollByTopValue = (rect: ClientRect, block: BlockPositionRelativeToWindow): number => {
    const { placement } = this.props
    const page = getAnchorPage(placement)
    const blockName = getAnchorBlock(placement as AnchorPlacement)
    // adjust scroll position if was navigated to block and `SCROLL_DELTA` contains the page
    const delta = page && page in SCROLL_DELTA && blockName ? SCROLL_DELTA[page] : 0
    switch (block) {
      case 'start':
        return rect.top - EMPIRIC_SCROLL_MARGIN - delta

      case 'end':
        return rect.bottom - window.innerHeight + EMPIRIC_SCROLL_MARGIN

      case 'nearest': {
        return this.getWindowScrollByTopValue(rect, this.resolveNearest(rect))
      }
    }
  }

  private scrollToAnchor = () => {
    const { activateOptions } = this.props
    const rootNode = this.rootRef.current
    if (!rootNode || !activateOptions) {
      return
    }

    const rect = rootNode.getBoundingClientRect()

    const { behavior, block, preventScrollWhenVisible } = activateOptions
    if (preventScrollWhenVisible && isElementInViewport(rootNode)) {
      return
    }

    // block scroll to differ our scroll from a user's one
    this.scrollIsBlocked = true
    window.scrollBy({
      left: 0,
      top: this.getWindowScrollByTopValue(rect, block),
      behavior: behavior === 'instant' ? 'auto' : behavior,
    })
  }

  private attachEventBindings = () => {
    if (this.state.isSubscribed) {
      return
    }

    this.setState({ isSubscribed: true })

    this.props.subtreeTracker.addSubtreeTrackingListener(this.scrollToAnchor)
    document.addEventListener('click', this.handleDocumentClick, true)
    document.addEventListener('touchstart', this.handleScrollThrottled)
    window.addEventListener('scroll', this.handleScrollThrottled)
  }

  private destroyEventBindings = () => {
    if (!this.state.isSubscribed) {
      return
    }

    if (this.props.subtreeTracker.removeSubtreeTrackingListener) {
      this.props.subtreeTracker.removeSubtreeTrackingListener(this.scrollToAnchor)
    }

    document.removeEventListener('click', this.handleDocumentClick, true)
    document.removeEventListener('touchstart', this.handleScrollThrottled)
    window.removeEventListener('scroll', this.handleScrollThrottled)

    this.props.discardActiveAnchor()

    this.setState({ isSubscribed: false })
  }

  private handleComponentUpdate = () => {
    const { activateOptions } = this.props

    if (!activateOptions || !this.props.subtreeTracker.addSubtreeTrackingListener) {
      return
    }

    this.scrollToAnchor()
    this.attachEventBindings()
  }

  private handleDocumentClick = () => {
    this.destroyEventBindings()
  }

  private handleScroll = () => {
    if (this.scrollIsBlocked) {
      // release scroll
      this.scrollIsBlocked = false
      return
    }

    this.destroyEventBindings()
  }
}

const mapStateToProps = (state: State, ownProps: AnchorOwnProps): AnchorStateProps => {
  const { activeAnchor } = state.app.navigation
  const { placement } = ownProps

  let options: null | AnchorActivationOptions = null
  if (activeAnchor && extractAnchor(activeAnchor.current) === extractAnchor(placement)) {
    options = activeAnchor.options
  }

  return {
    activateOptions: options,
  }
}

const mapDispatchToProps: AnchorDispatchProps = {
  discardActiveAnchor: () => activateAnchorActionCreator(null),
}

const AnchorConnected = connect(mapStateToProps, mapDispatchToProps)(AnchorComponent)

/**
 * @deprecated Use PageAnchorHandler
 */
export default withContext(SubtreeTrackerContext, 'subtreeTracker')(AnchorConnected)
